import React, { useState } from 'react';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Alert,
  LinearProgress,
  Divider
} from '@mui/material';
import {
  AttachMoney,
  AccountBalance,
  Description,
  TrendingUp,
  Add,
  CheckCircle,
  Cancel,
  Schedule,
  DirectionsCar,
  Person
} from '@mui/icons-material';
import { useBanking } from '../context/BankingContext';
import { useAuth } from '../context/AuthContext';
import { formatCurrency, formatDate } from '../utils/mockData';
import { Loan, LoanApplication } from '../types';

const Loans: React.FC = () => {
  const { 
    loans, 
    loanApplications, 
    applyLoan, 
    approveLoan, 
    declineLoan 
  } = useBanking();
  
  const { user } = useAuth();
  
  const [openApplyDialog, setOpenApplyDialog] = useState(false);
  const [selectedLoanType, setSelectedLoanType] = useState<'personal' | 'auto'>('personal');
  const [loanAmount, setLoanAmount] = useState('');
  const [loanPurpose, setLoanPurpose] = useState('');

  // Data is now filtered by user in BankingContext (except for admin users)
  const userLoans = loans;
  const userLoanApplications = loanApplications;
  const allLoanApplications = user?.role === 'admin' ? loanApplications : userLoanApplications;

  // Calculate loan statistics
  const totalLoanAmount = userLoans.reduce((sum, loan) => sum + loan.currentBalance, 0);
  const totalMonthlyPayments = userLoans.reduce((sum, loan) => sum + loan.monthlyPayment, 0);
  const activeLoanCount = userLoans.filter(loan => loan.status === 'active').length;
  const averageInterestRate = userLoans.length > 0 
    ? userLoans.reduce((sum, loan) => sum + loan.interestRate, 0) / userLoans.length 
    : 0;

  const handleApplyLoan = async () => {
    if (!loanAmount || !loanPurpose) return;
    
    try {
      await applyLoan(selectedLoanType, parseFloat(loanAmount), loanPurpose);
      setOpenApplyDialog(false);
      setLoanAmount('');
      setLoanPurpose('');
    } catch (error) {
      console.error('Failed to apply for loan:', error);
    }
  };

  const handleApproveLoan = async (applicationId: string) => {
    try {
      await approveLoan(applicationId);
    } catch (error) {
      console.error('Failed to approve loan:', error);
    }
  };

  const handleDeclineLoan = async (applicationId: string) => {
    try {
      await declineLoan(applicationId);
    } catch (error) {
      console.error('Failed to decline loan:', error);
    }
  };

  const getLoanTypeIcon = (type: 'personal' | 'auto') => {
    return type === 'auto' ? <DirectionsCar /> : <Person />;
  };

  const getLoanApplicationColor = (status: string) => {
    switch (status) {
      case 'pending': return '#ff9800';
      case 'approved': return '#4caf50';
      case 'declined': return '#f44336';
      default: return '#9e9e9e';
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          Loans
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => setOpenApplyDialog(true)}
          sx={{ borderRadius: 3 }}
        >
          Apply for Loan
        </Button>
      </Box>

      {/* Loan Statistics Dashboard */}
      <Box sx={{ display: 'flex', gap: 3, mb: 4, flexWrap: 'wrap' }}>
        <Card sx={{ flex: '1 1 250px', minWidth: '250px', borderRadius: 3, boxShadow: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Total Loan Balance
                </Typography>
                <Typography variant="h5" fontWeight="bold">
                  {formatCurrency(totalLoanAmount)}
                </Typography>
              </Box>
              <Box
                sx={{
                  backgroundColor: 'error.light',
                  borderRadius: '50%',
                  p: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <AttachMoney sx={{ color: 'error.main' }} />
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ flex: '1 1 250px', minWidth: '250px', borderRadius: 3, boxShadow: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Monthly Payments
                </Typography>
                <Typography variant="h5" fontWeight="bold">
                  {formatCurrency(totalMonthlyPayments)}
                </Typography>
              </Box>
              <Box
                sx={{
                  backgroundColor: 'warning.light',
                  borderRadius: '50%',
                  p: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <AccountBalance sx={{ color: 'warning.main' }} />
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ flex: '1 1 200px', minWidth: '200px', borderRadius: 3, boxShadow: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Active Loans
                </Typography>
                <Typography variant="h5" fontWeight="bold">
                  {activeLoanCount}
                </Typography>
              </Box>
              <Box
                sx={{
                  backgroundColor: 'info.light',
                  borderRadius: '50%',
                  p: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <Description sx={{ color: 'info.main' }} />
              </Box>
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ flex: '1 1 200px', minWidth: '200px', borderRadius: 3, boxShadow: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography color="textSecondary" gutterBottom variant="body2">
                  Avg Interest Rate
                </Typography>
                <Typography variant="h5" fontWeight="bold">
                  {averageInterestRate.toFixed(1)}%
                </Typography>
              </Box>
              <Box
                sx={{
                  backgroundColor: 'success.light',
                  borderRadius: '50%',
                  p: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                <TrendingUp sx={{ color: 'success.main' }} />
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
        {/* Active Loans */}
        <Box sx={{ flex: '2 1 400px', minWidth: '400px' }}>
          <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                My Active Loans
              </Typography>
              {userLoans.length === 0 ? (
                <Box textAlign="center" py={4}>
                  <Typography color="textSecondary">
                    No active loans found
                  </Typography>
                </Box>
              ) : (
                <List>
                  {userLoans.map((loan: Loan, index: number) => {
                    const progressValue = ((loan.principalAmount - loan.currentBalance) / loan.principalAmount) * 100;
                    
                    return (
                      <React.Fragment key={loan.id}>
                        <ListItem sx={{ px: 0 }}>
                          <Box sx={{ mr: 2 }}>
                            {getLoanTypeIcon(loan.loanType)}
                          </Box>
                          <ListItemText
                            primary={
                              <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Typography variant="subtitle1" fontWeight="bold">
                                  {loan.loanType === 'auto' ? 'Auto Loan' : 'Personal Loan'}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    backgroundColor: getLoanApplicationColor(loan.status),
                                    color: 'white',
                                    px: 1,
                                    py: 0.5,
                                    borderRadius: 1,
                                    fontSize: '0.75rem'
                                  }}
                                >
                                  {loan.status.replace('_', ' ').toUpperCase()}
                                </Typography>
                              </Box>
                            }
                            secondary={
                              <Box mt={1}>
                                <Box sx={{ display: 'flex', gap: 4, mb: 2 }}>
                                  <Box>
                                    <Typography variant="body2" color="textSecondary">
                                      Balance: {formatCurrency(loan.currentBalance)}
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                      Monthly Payment: {formatCurrency(loan.monthlyPayment)}
                                    </Typography>
                                  </Box>
                                  <Box>
                                    <Typography variant="body2" color="textSecondary">
                                      Interest Rate: {loan.interestRate}%
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary">
                                      Created: {formatDate(loan.createdAt)}
                                    </Typography>
                                  </Box>
                                </Box>
                                <Box mt={2}>
                                  <Typography variant="body2" color="textSecondary" gutterBottom>
                                    Loan Progress ({progressValue.toFixed(1)}% paid)
                                  </Typography>
                                  <LinearProgress 
                                    variant="determinate" 
                                    value={progressValue} 
                                    sx={{ height: 8, borderRadius: 4 }}
                                  />
                                </Box>
                              </Box>
                            }
                            secondaryTypographyProps={{ component: 'div' }}
                          />
                        </ListItem>
                        {index < userLoans.length - 1 && <Divider />}
                      </React.Fragment>
                    );
                  })}
                </List>
              )}
            </CardContent>
          </Card>
        </Box>

        {/* Loan Applications */}
        <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
          <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" gutterBottom>
                {user?.role === 'admin' ? 'All Loan Applications' : 'My Applications'}
              </Typography>
              {allLoanApplications.length === 0 ? (
                <Box textAlign="center" py={4}>
                  <Typography color="textSecondary">
                    No loan applications found
                  </Typography>
                </Box>
              ) : (
                <List dense>
                  {allLoanApplications.map((application: LoanApplication, index: number) => (
                    <React.Fragment key={application.id}>
                      <ListItem sx={{ px: 0 }}>
                        <Box sx={{ mr: 2 }}>
                          {application.status === 'pending' && <Schedule color="warning" />}
                          {application.status === 'approved' && <CheckCircle color="success" />}
                          {application.status === 'declined' && <Cancel color="error" />}
                        </Box>
                        <ListItemText
                          primary={
                            <Typography variant="subtitle2" fontWeight="bold">
                              {application.loanType === 'auto' ? 'Auto Loan' : 'Personal Loan'}
                            </Typography>
                          }
                          secondary={
                            <Box>
                              <Typography variant="body2" color="textSecondary">
                                {formatCurrency(application.amount)}
                              </Typography>
                              <Typography variant="body2" color="textSecondary">
                                {application.purpose}
                              </Typography>
                              <Typography variant="body2" color="textSecondary">
                                {formatDate(application.appliedAt)}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: getLoanApplicationColor(application.status),
                                  fontWeight: 'bold',
                                  mt: 0.5
                                }}
                              >
                                {application.status.toUpperCase()}
                              </Typography>
                            </Box>
                          }
                          secondaryTypographyProps={{ component: 'div' }}
                        />
                        {user?.role === 'admin' && application.status === 'pending' && (
                          <ListItemSecondaryAction>
                            <Box display="flex" gap={1}>
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => handleApproveLoan(application.id)}
                              >
                                <CheckCircle />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeclineLoan(application.id)}
                              >
                                <Cancel />
                              </IconButton>
                            </Box>
                          </ListItemSecondaryAction>
                        )}
                      </ListItem>
                      {index < allLoanApplications.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Apply for Loan Dialog */}
      <Dialog 
        open={openApplyDialog} 
        onClose={() => setOpenApplyDialog(false)}
        maxWidth="sm"
        fullWidth
        disableRestoreFocus
      >
        <DialogTitle sx={{ fontWeight: 'bold' }}>
          Apply for a Loan
        </DialogTitle>
        <DialogContent>
          <Box mt={2}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Loan Type</InputLabel>
              <Select
                value={selectedLoanType}
                onChange={(e) => setSelectedLoanType(e.target.value as 'personal' | 'auto')}
                label="Loan Type"
              >
                <MenuItem value="personal">Personal Loan</MenuItem>
                <MenuItem value="auto">Auto Loan</MenuItem>
              </Select>
            </FormControl>

            <TextField
              fullWidth
              margin="normal"
              label="Loan Amount"
              type="number"
              value={loanAmount}
              onChange={(e) => setLoanAmount(e.target.value)}
              InputProps={{ startAdornment: '$' }}
            />

            <TextField
              fullWidth
              margin="normal"
              label="Purpose"
              multiline
              rows={3}
              value={loanPurpose}
              onChange={(e) => setLoanPurpose(e.target.value)}
              placeholder="Describe the purpose of this loan..."
            />

            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Loan Terms:</strong><br />
                • Personal Loans: Up to $50,000, 5.5% - 12% APR<br />
                • Auto Loans: Up to $100,000, 3.5% - 8% APR<br />
                • Quick approval process (24-48 hours)
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpenApplyDialog(false)}>
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleApplyLoan}
            disabled={!loanAmount || !loanPurpose}
            sx={{ borderRadius: 2 }}
          >
            Submit Application
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Loans; 